/* This component uses cockpit layout as base so if cockpit layout changes this component should change too! */
import {
    Flex,
    Text,
    Box,
    useTheme,
    useBreakpointValue,
    Skeleton,
    HStack,
    Link
} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import React, {useEffect, useState, useRef, useCallback, useMemo} from 'react'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {useHeader} from '../../../hooks/use-header'
import AddToCartCTA from '../../../components/add-to-cart-cta'
import useMultiSite from '../../../hooks/use-multi-site'
import {getProductsToAddToCart, isProductSet} from '../../../utils/product-utils'
import ProductDisplayPrice from '../../../components/product-display-price'
import {css, Global} from '@emotion/react'

const messages = defineMessages({
    overview: {defaultMessage: 'Overview', id: 'product_detail.sections.overview'},
    details: {defaultMessage: 'Details', id: 'product_detail.sections.details'},
    specifications: {defaultMessage: 'Specifications', id: 'pdp.module.specifications'},
    accessories: {
        defaultMessage: 'Accessories',
        id: 'product_detail.recommended_products.title.accessories'
    },
    moreInfos: {defaultMessage: 'More Infos', id: 'product_detail.sections.moreInfos'},
    reviews: {defaultMessage: 'Reviews', id: 'product_detail.sections.reviews'},
    howToUse: {defaultMessage: 'How To Use', id: 'product_detail.sections.howToUse'},
    spareParts: {defaultMessage: 'Spare Parts', id: 'product_detail.sections.spareParts'},
    productDetailsMenu: {
        defaultMessage: 'Product Details Menu',
        id: 'product_detail.sections.productDetailsMenu'
    }
})

const PdpSubnavigation = ({
    isGetPropsLoading,
    isPromotionsLoading,
    isVariantLoading,
    product,
    quantity,
    sections
}) => {
    const {site} = useMultiSite()
    const {formatMessage} = useIntl()
    const {header, headerWrapper} = useHeader()
    const headerWrapperHeight = headerWrapper.clientHeight
    const subnavigationHeight = headerWrapper.clientHeight - header.clientHeight
    const theme = useTheme()
    const scrollTop = useRef(0)
    const [active, setActive] = useState(null)
    const [hiddenHeader, setHiddenHeader] = useState(false)
    const [show, setShow] = useState(false)
    const mobile = useBreakpointValue({base: true, lg: false})
    const isProductASet = isProductSet(product)
    /* Anchor offset according dynamic header height */
    const anchorOffset = useRef()
    anchorOffset.current = hiddenHeader ? subnavigationHeight : headerWrapperHeight

    // we need use a ref here because we load some components async and
    // this allows the handleScroll function to always have the current filtered sections
    const filteredSections = useRef([])
    filteredSections.current = Object.fromEntries(
        Object.entries(sections).filter((arr) => arr?.[1]?.current)
    )

    const totalProducts = useMemo(() => getProductsToAddToCart(product).length, [product])

    /* Subnavigation links onclick - scroll to selected anchor of PDP */
    const scrollToAnchor = (id) => {
        const element = filteredSections.current[id].current

        if (element) {
            const scrollingDownwards = scrollTop.current < element.offsetTop
            anchorOffset.current = scrollingDownwards ? subnavigationHeight : headerWrapperHeight

            window.scrollTo({
                top: element.offsetTop - anchorOffset.current,
                behavior: 'smooth'
            })
        }
    }

    const handleScroll = useCallback(() => {
        requestAnimationFrame(() => {
            setHiddenHeader(scrollTop.current <= scrollY)
            setShow(scrollY > headerWrapperHeight * 2)

            scrollTop.current = scrollY

            /* Check if we reached a section and update active state of subnavigation */
            Object.entries(filteredSections.current).map(([key, {current}]) => {
                if (current) {
                    const start = current.offsetTop - anchorOffset.current
                    const end = current.offsetTop + current.clientHeight - anchorOffset.current

                    if (scrollY >= start && scrollY <= end) {
                        setActive(key)
                    }
                }
            })
        })
    }, [headerWrapperHeight])

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, {
            passive: true
        })

        /* Cleanup event listener */
        return () => window.removeEventListener('scroll', handleScroll)
    }, [handleScroll])

    // keep track of the height of the subnavigation - initial value as per design
    // using a state causes additional rerender which is causing a flicker, so ref is used
    // exposed as css var --subnavigation-height at the :root level for use in other components
    const heightRef = useRef(53)

    return (
        <Box
            maxWidth={show ? '100%' : 'container.xxl'}
            mx="auto"
            bg="stokkeGray.bright"
            boxShadow={theme.shadows.bottom}
            // update the component height on render when it's visible
            ref={(e) => {
                if (e?.clientHeight) {
                    heightRef.current = e.clientHeight
                }
            }}
        >
            {/* expose the component height as css var - currently used for top positioned toasts */}
            <Global
                styles={css`
                    :root {
                        --subnavigation-height: ${show ? heightRef.current : 0}px;
                    }
                `}
            />
            <Box
                padding={{
                    base: 4,
                    lg: '0.75rem 1rem 0.75rem 1.5rem'
                }}
                background="stokkeGray.bright"
                overflowX={{
                    base: 'auto',
                    lg: 'hidden'
                }}
                transition=".5s"
                sx={{
                    '&>*': {
                        flexShrink: 0
                    }
                }}
                scrollBehavior="smooth"
                maxWidth="container.xxl"
                mx="auto"
            >
                <Flex justify="space-between" align="center">
                    <Flex gap={4} as="nav" aria-label={formatMessage(messages.productDetailsMenu)}>
                        {Object.keys(filteredSections.current).map((section) => (
                            <Link
                                key={section}
                                size="sm"
                                variant="core"
                                textDecoration={active === section ? 'underline' : 'none'}
                                aria-current={active === section ? 'true' : 'false'}
                                cursor="pointer"
                                _hover={mobile ? {} : null}
                                onClick={() => {
                                    scrollToAnchor(section)
                                }}
                                role="link"
                                tabIndex="0"
                            >
                                <FormattedMessage {...messages[section]} />
                            </Link>
                        ))}
                    </Flex>
                    <Box display={{base: 'none', lg: 'block'}}>
                        <Flex gap={2} align="center" justifyContent="flex-end">
                            <Flex flexDirection="column">
                                {isProductASet && (
                                    <HStack
                                        justify="start"
                                        alignItems="flex-start"
                                        spacing={0}
                                        gap={1.5}
                                    >
                                        <Text textStyle="Regular Extra Small">
                                            <FormattedMessage
                                                id="global.total"
                                                defaultMessage="Total"
                                            />
                                        </Text>
                                        <Skeleton isLoaded={!isGetPropsLoading}>
                                            <Text
                                                textStyle="Regular Extra Small"
                                                whiteSpace="nowrap"
                                                color="stokkeGray.accessibleText"
                                            >
                                                <FormattedMessage
                                                    id="product_set_detail.cockpit.product-count"
                                                    defaultMessage="{totalProducts} products"
                                                    values={{totalProducts}}
                                                />
                                            </Text>
                                        </Skeleton>
                                    </HStack>
                                )}
                                {!site?.isContentOnlySite && (
                                    <Flex
                                        flexDirection="column"
                                        flexBasis="min-content"
                                        alignItems="end"
                                    >
                                        <ProductDisplayPrice
                                            product={product}
                                            loading={
                                                isGetPropsLoading ||
                                                isPromotionsLoading ||
                                                isVariantLoading
                                            }
                                            variant="stickyCta"
                                        />
                                    </Flex>
                                )}
                            </Flex>
                            <Flex flexBasis="content">
                                <AddToCartCTA
                                    isLoading={
                                        isGetPropsLoading || isPromotionsLoading || isVariantLoading
                                    }
                                    product={product}
                                    quantity={quantity}
                                />
                            </Flex>
                        </Flex>
                    </Box>
                </Flex>
            </Box>
        </Box>
    )
}

PdpSubnavigation.propTypes = {
    isGetPropsLoading: PropTypes.bool,
    isPromotionsLoading: PropTypes.bool,
    isVariantLoading: PropTypes.bool,
    product: PropTypes.object,
    quantity: PropTypes.number,
    sections: PropTypes.object
}

export default PdpSubnavigation
